import { Controller } from "@hotwired/stimulus"

import { clearPickerValueInputs } from "../utils/cloned_field_reset"
import { limitEntryBlockAllowanceHours } from "../utils/per_entry_allowance_limit"
import { minutesToHHMM, toMinutes } from "../utils/timesheet_net_time"

// Every edit that can move a block's times: typing, a picker's change, and the
// focusout that settles a Pause-Block's end (#chainBreakRow).
const LIMIT_EVENTS = ["input", "change", "focusout"]

// Duplicatable entry blocks of a Mehrfacheintrag form.
//
// With "Einträge lückenlos aneinanderreihen" (chain value) every further block
// starts where the previous one — or the Pause-Block between them — ends: its
// Beginn is shown read-only and follows live. A Pause-Block is a row of this
// controller's own, never submitted as such: on submit (#foldBreaks, on the
// form's formdata event) it is written onto the block it follows, whose window
// then runs over the pause and which carries the pause as its break — a von/bis
// row where the template collects them (periodsMode), otherwise minutes on its
// Pause. The server stores exactly that, so the API knows nothing of break rows.
// Folding into the FormData rather than the inputs keeps the visible form as
// the employee entered it, and a re-rendered or resubmitted form cannot fold twice.
export default class extends Controller {
  static targets = ["entry", "addButton", "container", "breakButton"]
  static values = {
    maxEntries: { type: Number, default: 5 },
    entryLabel: { type: String, default: "Eintrag" },
    defaultTimeTo: { type: String, default: "" },
    chain: { type: Boolean, default: false },
    periodsMode: { type: Boolean, default: false },
    breakMinutes: { type: Number, default: 30 },
    breakLabel: { type: String, default: "Pause" },
    breakToLabel: { type: String, default: "bis" },
    chainedHint: { type: String, default: "" },
    removeBreakLabel: { type: String, default: "Pause entfernen" },
  }

  connect() {
    this.entryIndex = 0
    this.updateHeaders()
    if (this.chainValue) {
      this._form = this.element.closest("form")
      this._chain = () => this.chainBlocks()
      this._fold = (event) => this.foldBreaks(event.formData)
      this.element.addEventListener("input", this._chain)
      this.element.addEventListener("change", this._chain)
      // A Pause-Block's end is corrected only once the employee leaves it — see #chainBreakRow.
      this.element.addEventListener("focusout", this._chain)
      this._form?.addEventListener("formdata", this._fold)
      this.chainBlocks()
    }

    // "Pro Eintrag" Zulagen: each block's hours may not exceed its own work time.
    // Registered after the chaining listeners, so a block whose start moves with
    // the one before it is measured on its chained times, not the previous ones.
    this._limitAllowances = () => limitEntryBlockAllowanceHours(this.element)
    LIMIT_EVENTS.forEach((type) => this.element.addEventListener(type, this._limitAllowances))
    this._limitAllowances()
  }

  disconnect() {
    LIMIT_EVENTS.forEach((type) => this.element.removeEventListener(type, this._limitAllowances))
    if (!this._chain) return

    this.element.removeEventListener("input", this._chain)
    this.element.removeEventListener("change", this._chain)
    this.element.removeEventListener("focusout", this._chain)
    this._form?.removeEventListener("formdata", this._fold)
  }

  add() {
    const firstEntry = this.entryTargets[0]
    if (!firstEntry) return

    this.entryIndex++
    const clone = firstEntry.cloneNode(true)

    // Update entry index and rewrite input names/ids
    this.rewriteClonedEntry(clone, this.entryIndex)

    // Clear values in cloned entry
    this.clearValues(clone)

    // Cascade time values from the last existing entry to the new clone
    const lastEntry = this.entryTargets[this.entryTargets.length - 1]
    this.cascadeTimeValues(lastEntry, clone)

    // Handle SlimSelect: remove .ss-main wrappers and restore original selects
    this.resetSlimSelects(clone)

    // Append to container
    this.containerTarget.appendChild(clone)

    // Update all headers
    this.updateHeaders()

    this.syncAddButtons()
    this.chainBlocks()
    this._limitAllowances()
    this.notifyDynamicForms()
  }

  // "Pause einfügen": a Pause-Block after the last block, and the block that
  // follows it, in one step — a pause at the end of the day is no pause.
  addWithBreak() {
    if (this.entryTargets.length >= this.maxEntriesValue) return

    this.containerTarget.appendChild(this.buildBreakRow())
    this.add()
  }

  removeBreak(event) {
    event.target.closest("[data-multi-entry-break-row]")?.remove()
    this.chainBlocks()
    this._limitAllowances()
  }

  syncAddButtons() {
    const full = this.entryTargets.length >= this.maxEntriesValue
    this.addButtonTarget.classList.toggle("hidden", full)
    if (this.hasBreakButtonTarget) this.breakButtonTarget.classList.toggle("hidden", full)
  }

  remove(event) {
    const entry = event.target.closest("[data-multi-entry-target='entry']")
    if (!entry) return

    // Don't remove the first entry
    if (this.entryTargets.indexOf(entry) === 0) return

    // A Pause-Block belongs to the block it leads into.
    const before = entry.previousElementSibling
    if (before?.hasAttribute("data-multi-entry-break-row")) before.remove()

    entry.remove()

    // Reindex remaining additional entries
    this.reindexEntries()

    // Update headers
    this.updateHeaders()

    this.syncAddButtons()
    this.chainBlocks()
    this._limitAllowances()
    this.notifyDynamicForms()
  }

  // Walks the blocks in order and lets each further block start where the one
  // before it — or the Pause-Block in between — ends. Values are written without
  // events (a change would re-enter here); listeners that mirror the day hear
  // about it through multi-entry:chained instead.
  chainBlocks() {
    // A time picker's write dispatches change (see #setTimeValue), which lands here
    // again mid-walk; the outer walk finishes the job.
    if (!this.chainValue || this._chaining) return

    this._chaining = true
    try {
      this.walkChain()
    } finally {
      this._chaining = false
    }
    this.element.dispatchEvent(new CustomEvent("multi-entry:chained", { bubbles: true }))
  }

  walkChain() {
    let cursor = null
    let previousEntry = null
    ;[...this.containerTarget.children].forEach((child) => {
      if (child.hasAttribute("data-multi-entry-break-row")) {
        cursor = this.chainBreakRow(child, cursor, previousEntry)
        return
      }
      if (!this.entryTargets.includes(child)) return

      if (this.entryTargets.indexOf(child) > 0) {
        if (cursor !== null) this.setTimeValue(child, "time_from", minutesToHHMM(cursor))
        this.lockTimeFrom(child)
      }
      const end = this.getTimeValue(child, "time_to")
      cursor = end ? this.endMinutes(end) : null
      previousEntry = child
    })
  }

  // A Pause-Block starts where the block before it ends and keeps its length when
  // that end moves. Returns where the next block starts.
  chainBreakRow(row, cursor, previousEntry) {
    const toInput = row.querySelector("[data-break-to]")
    const fromDisplay = row.querySelector("[data-break-from]")
    row.dataset.afterPrefix = previousEntry ? this.entryPrefix(previousEntry) : ""
    if (cursor === null) {
      fromDisplay.textContent = "--:--"
      return toMinutes(toInput.value) || null
    }

    const previousFrom = row.dataset.from ? Number(row.dataset.from) : null
    let to = toInput.value ? toMinutes(toInput.value) : null
    // While the end is being typed a native time input reports every keystroke as a
    // complete value ("1" of 13:00 reads as 01:30). Correcting that would yank the
    // field back mid-typing, so a focused field is left alone and the chain runs on
    // with its last good end; focusout settles it.
    if (document.activeElement === toInput && previousFrom === cursor) {
      if (to === null || to <= cursor)
        to = Number(row.dataset.to) || cursor + this.breakMinutesValue
      row.dataset.to = String(to)
      fromDisplay.textContent = minutesToHHMM(cursor)
      return to
    }
    if (to === null || to <= cursor || (previousFrom !== null && previousFrom !== cursor)) {
      const length =
        previousFrom !== null && to !== null && to > previousFrom
          ? to - previousFrom
          : this.breakMinutesValue
      to = Math.min(cursor + length, 1439)
      toInput.value = minutesToHHMM(to)
    }
    row.dataset.from = String(cursor)
    row.dataset.to = String(to)
    fromDisplay.textContent = minutesToHHMM(cursor)
    return to
  }

  // A chained block's Beginn is the previous end: shown, not editable. The
  // inputs stay in the form (hidden, not disabled) so the value still submits.
  lockTimeFrom(entry) {
    const field = entry.querySelector('[data-chain-field="time_from"]')
    if (!field) return

    field.classList.add("hidden")
    let display = entry.querySelector("[data-chain-from-display]")
    if (!display) {
      display = document.createElement("p")
      display.className = "mt-2 text-sm text-gray-700"
      display.dataset.chainFromDisplay = "true"
      field.insertAdjacentElement("afterend", display)
    }
    const value = this.getTimeValue(entry, "time_from") || "--:--"
    display.textContent = `${this.chainedHintValue} ${value}`.trim()
  }

  buildBreakRow() {
    const row = document.createElement("div")
    row.dataset.multiEntryBreakRow = "true"
    row.className =
      "mb-4 flex flex-wrap items-center gap-3 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 px-4 py-3"

    const label = document.createElement("span")
    label.className = "text-sm font-semibold text-amber-900"
    label.textContent = this.breakLabelValue

    const from = document.createElement("span")
    from.className = "text-sm text-amber-900 tabular-nums"
    from.dataset.breakFrom = "true"
    from.textContent = "--:--"

    const toLabel = document.createElement("label")
    toLabel.className = "flex items-center gap-2 text-sm text-amber-900"
    toLabel.textContent = this.breakToLabelValue
    // No name: the row itself never submits — #foldBreaks writes it onto the
    // block it follows.
    const to = document.createElement("input")
    to.type = "time"
    to.dataset.breakTo = "true"
    to.className =
      "rounded-md border-gray-300 text-sm shadow-xs focus:border-green-500 focus:ring-green-500"
    toLabel.appendChild(to)

    const remove = document.createElement("button")
    remove.type = "button"
    remove.className =
      "ml-auto inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100 focus:outline-hidden"
    remove.dataset.action = "click->multi-entry#removeBreak"
    remove.innerHTML =
      '<svg class="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>'
    remove.append(this.removeBreakLabelValue)

    row.append(label, from, toLabel, remove)
    return row
  }

  // Writes every Pause-Block onto the block it follows, in the submitted data
  // only. Von/bis rows need a NUMERIC child index — strong params drop any
  // other key of a nested-attributes hash.
  foldBreaks(formData) {
    let index = Date.now()
    this.containerTarget.querySelectorAll("[data-multi-entry-break-row]").forEach((row) => {
      const prefix = row.dataset.afterPrefix
      const from = Number(row.dataset.from)
      const to = Number(row.dataset.to)
      if (!prefix || !(to > from)) return

      formData.set(`${prefix}[time_to]`, minutesToHHMM(to))
      if (this.periodsModeValue) {
        const key = `${prefix}[configurable_time_sheet_break_periods_attributes][${index}]`
        index += 1
        formData.append(`${key}[break_from]`, minutesToHHMM(from))
        formData.append(`${key}[break_to]`, minutesToHHMM(to))
        return
      }

      const minutes = to - from
      formData.set(
        `${prefix}[time_break]`,
        minutesToHHMM(toMinutes(formData.get(`${prefix}[time_break]`)) + minutes),
      )
      const declared = formData.get(`${prefix}[manual_break_minutes]`)
      if (declared !== null && declared !== "") {
        formData.set(`${prefix}[manual_break_minutes]`, String(Number(declared) + minutes))
      }
    })
  }

  // The name prefix of an entry block: configurable_time_sheet for the first,
  // additional_entries[N] for a clone (N is not its position — see #reindexEntries).
  entryPrefix(entry) {
    const input = [...entry.querySelectorAll("input[name]")].find((i) =>
      /\[time_to\]$/.test(i.name),
    )
    return input ? input.name.replace(/\[time_to\]$/, "") : ""
  }

  // 00:00 as an end means end of day, as everywhere in the timesheet forms.
  endMinutes(value) {
    const minutes = toMinutes(value)
    return minutes === 0 ? 1440 : minutes
  }

  // A form-level conditional field may read a per-entry key across all blocks,
  // so its visibility depends on which blocks exist. That scope sits outside this
  // controller's element and adding/removing a block fires no input/change, so
  // tell it to re-evaluate explicitly — otherwise a field stays shown (and
  // required) for a block the user just deleted.
  notifyDynamicForms() {
    document.dispatchEvent(new CustomEvent("dynamic-form:refresh"))
  }

  rewriteClonedEntry(clone, index) {
    // Rewrite input names: configurable_time_sheet[X] -> additional_entries[N][X]
    // and per-entry custom fields: custom_fields[X] -> additional_entries[N][custom_fields][X]
    // and "Pro Eintrag" Zulagen: allowances[X] -> additional_entries[N][allowances][X]
    const inputs = clone.querySelectorAll("input, select, textarea")
    inputs.forEach((input) => {
      if (input.name) {
        input.name = input.name
          .replace(/^configurable_time_sheet\[([^\]]+)\]/, `additional_entries[${index}][$1]`)
          .replace(/^custom_fields\[/, `additional_entries[${index}][custom_fields][`)
          .replace(/^allowances\[/, `additional_entries[${index}][allowances][`)
      }
      if (input.id) {
        input.id = `additional_entry_${index}_${input.id.replace(/^configurable_time_sheet_/, "")}`
      }
    })

    // Per-entry custom fields carry their own dynamic-form (conditional
    // visibility) scope — point the clone's scope at its own namespace so
    // conditions between per-entry fields evaluate inside this block.
    clone.querySelectorAll("[data-dynamic-form-name-prefix-value]").forEach((scope) => {
      scope.setAttribute(
        "data-dynamic-form-name-prefix-value",
        `additional_entries[${index}][custom_fields]`,
      )
    })

    // Update task wrapper IDs for site-tasks controller
    const taskWrapper = clone.querySelector("[id$='_task_ids_wrapper']")
    if (taskWrapper) {
      taskWrapper.id = `additional_entry_${index}_task_ids_wrapper`
    }

    const taskSelect = clone.querySelector("select[id$='_task_ids']")
    if (taskSelect) {
      taskSelect.id = `additional_entry_${index}_task_ids`
    }

    // Update site select data attributes for site-tasks controller
    const siteSelect = clone.querySelector("select[data-model='configurable_timesheet']")
    if (siteSelect) {
      siteSelect.dataset.wrapperId = `additional_entry_${index}_task_ids_wrapper`
      siteSelect.dataset.dropdownId = `additional_entry_${index}_task_ids`
    }

    // Update label for attributes
    const labels = clone.querySelectorAll("label[for]")
    labels.forEach((label) => {
      const forAttr = label.getAttribute("for")
      if (forAttr?.startsWith("configurable_time_sheet_")) {
        const fieldPart = forAttr.replace("configurable_time_sheet_", "")
        label.setAttribute("for", `additional_entry_${index}_${fieldPart}`)
      } else if (forAttr?.startsWith("custom_fields_")) {
        // Per-entry custom field labels follow their inputs' rewritten ids
        label.setAttribute("for", `additional_entry_${index}_${forAttr}`)
      }
    })

    // Nested-form templates (e.g. break periods) hold inert HTML the input loop
    // above can't see. Rewrite their markup so rows added inside this cloned
    // entry are namespaced to additional_entries[N] instead of the base record.
    clone.querySelectorAll("template").forEach((tpl) => {
      tpl.innerHTML = tpl.innerHTML
        .replaceAll('name="configurable_time_sheet[', `name="additional_entries[${index}][`)
        .replaceAll('id="configurable_time_sheet_', `id="additional_entry_${index}_`)
        .replaceAll('for="configurable_time_sheet_', `for="additional_entry_${index}_`)
    })

    // Cloned entries start with no pre-filled nested rows (e.g. break periods,
    // Fahrzeiten); each entry's nested times are independent.
    clone.querySelectorAll("[data-break-period-row]").forEach((row) => row.remove())
    clone.querySelectorAll("[data-driving-time-row]").forEach((row) => row.remove())

    // A hint visible on the source entry would otherwise be cloned in its shown
    // state and describe minutes the new (empty) entry never had.
    clone.querySelectorAll("[data-break-rule-hint]").forEach((hint) => hint.classList.add("hidden"))
  }

  clearValues(clone) {
    const inputs = clone.querySelectorAll(
      "input[type='text'], input[type='time'], input[type='number'], input[type='date'], input[type='datetime-local']",
    )
    inputs.forEach((input) => {
      input.value = ""
    })

    // Per-entry custom fields: clear textareas and uncheck checkbox /
    // multi-select boxes (their hidden "false" companions stay untouched).
    clone.querySelectorAll("textarea").forEach((textarea) => {
      textarea.value = ""
    })
    clone.querySelectorAll("input[type='checkbox']").forEach((checkbox) => {
      checkbox.checked = false
    })
    // A "Ganzer Tag" the source block had ticked locked its hours field; the clone's is unticked.
    clone.querySelectorAll("[data-allowance-input-target='hoursInput']").forEach((input) => {
      input.readOnly = false
    })

    clearPickerValueInputs(clone)

    const selects = clone.querySelectorAll("select")
    selects.forEach((select) => {
      select.selectedIndex = 0
    })

    // Hide task wrapper in clone (no site selected yet)
    const taskWrapper = clone.querySelector("[id$='_task_ids_wrapper']")
    if (taskWrapper) {
      taskWrapper.classList.add("hidden")
    }

    // Reset task-select siteId on cloned entries
    const taskSelect = clone.querySelector("[data-controller*='task-select']")
    if (taskSelect) {
      taskSelect.dataset.taskSelectSiteIdValue = "0"
    }
  }

  resetSlimSelects(clone) {
    // Remove SlimSelect v3 wrapper elements (.ss-main)
    const ssWrappers = clone.querySelectorAll(".ss-main")
    ssWrappers.forEach((wrapper) => wrapper.remove())

    // Restore original select elements hidden by SlimSelect v3's hideUI()
    // which uses inline styles (position:absolute, width:1px, opacity:0, etc.)
    const selects = clone.querySelectorAll("select")
    selects.forEach((select) => {
      select.style.cssText = ""
      select.removeAttribute("tabindex")
      select.removeAttribute("aria-hidden")
      delete select.dataset.id
      select.classList.remove("ss-hide")
    })
  }

  updateHeaders() {
    this.entryTargets.forEach((entry, idx) => {
      // Remove all existing headers
      entry.querySelectorAll("[data-entry-header]").forEach((h) => h.remove())

      // Add header to all entries when there are multiple
      if (this.entryTargets.length > 1) {
        const header = document.createElement("div")
        header.className = "flex items-center justify-between mb-3"
        header.dataset.entryHeader = "true"

        const label = document.createElement("span")
        label.className = "text-sm font-semibold text-gray-800"
        label.textContent = `${this.entryLabelValue} ${idx + 1}`

        header.appendChild(label)

        // Add remove button for non-primary entries
        if (idx > 0) {
          const removeBtn = document.createElement("button")
          removeBtn.type = "button"
          removeBtn.className =
            "inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100 focus:outline-hidden"
          removeBtn.dataset.action = "click->multi-entry#remove"
          removeBtn.innerHTML =
            '<svg class="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>' +
            this.removeLabel
          header.appendChild(removeBtn)
        }

        entry.insertBefore(header, entry.firstChild)
      }
    })
  }

  reindexEntries() {
    this.entryTargets.forEach((entry, idx) => {
      if (idx === 0) return // Skip primary entry

      const inputs = entry.querySelectorAll("input, select, textarea")
      inputs.forEach((input) => {
        if (input.name) {
          input.name = input.name.replace(/additional_entries\[\d+\]/, `additional_entries[${idx}]`)
        }
        if (input.id) {
          input.id = input.id.replace(/additional_entry_\d+_/, `additional_entry_${idx}_`)
        }
      })

      // Update wrapper/dropdown IDs
      const taskWrapper = entry.querySelector("[id*='_task_ids_wrapper']")
      if (taskWrapper) {
        taskWrapper.id = `additional_entry_${idx}_task_ids_wrapper`
      }

      const taskSelect = entry.querySelector("select[id*='_task_ids']")
      if (taskSelect) {
        taskSelect.id = `additional_entry_${idx}_task_ids`
      }

      const siteSelect = entry.querySelector("select[data-model='configurable_timesheet']")
      if (siteSelect) {
        siteSelect.dataset.wrapperId = `additional_entry_${idx}_task_ids_wrapper`
        siteSelect.dataset.dropdownId = `additional_entry_${idx}_task_ids`
      }

      // Update labels
      const labels = entry.querySelectorAll("label[for]")
      labels.forEach((label) => {
        const forAttr = label.getAttribute("for")
        if (forAttr?.startsWith("additional_entry_")) {
          label.setAttribute(
            "for",
            forAttr.replace(/additional_entry_\d+_/, `additional_entry_${idx}_`),
          )
        }
      })

      // Reindex nested-form templates (e.g. break periods) too.
      entry.querySelectorAll("template").forEach((tpl) => {
        tpl.innerHTML = tpl.innerHTML
          .replace(/name="additional_entries\[\d+\]/g, `name="additional_entries[${idx}]`)
          .replace(/id="additional_entry_\d+_/g, `id="additional_entry_${idx}_`)
          .replace(/for="additional_entry_\d+_/g, `for="additional_entry_${idx}_`)
      })

      // Reindex the per-entry custom fields' dynamic-form scope.
      entry.querySelectorAll("[data-dynamic-form-name-prefix-value]").forEach((scope) => {
        scope.setAttribute(
          "data-dynamic-form-name-prefix-value",
          `additional_entries[${idx}][custom_fields]`,
        )
      })
    })
  }

  cascadeTimeValues(previousEntry, clonedEntry) {
    const previousTimeTo = this.getTimeValue(previousEntry, "time_to")
    if (!previousTimeTo) return

    this.setTimeValue(clonedEntry, "time_from", previousTimeTo)

    const defaultEnd = this.defaultTimeToValue
    if (defaultEnd && previousTimeTo < defaultEnd) {
      this.setTimeValue(clonedEntry, "time_to", defaultEnd)
    } else {
      this.setTimeValue(clonedEntry, "time_to", "")
    }

    this.setBreakTime(clonedEntry, "00:00")
  }

  getTimeValue(entry, fieldSuffix) {
    // Plus-minus / time-stepper: hidden field is the source of truth
    const hidden = entry.querySelector(
      `input[type="hidden"][name$="[${fieldSuffix}]"][data-time-stepper-target="hidden"]`,
    )
    if (hidden) return hidden.value || ""

    // Native time input
    const timeInput = entry.querySelector(`input[type="time"][name$="[${fieldSuffix}]"]`)
    if (timeInput) return timeInput.value || ""

    // Text input (timepicker controller)
    const textInput = entry.querySelector(`input[type="text"][name$="[${fieldSuffix}]"]`)
    if (textInput) return textInput.value || ""

    // A read-only field renders as a plain hidden input
    const plain = entry.querySelector(`input[name$="[${fieldSuffix}]"]`)
    return plain?.value || ""
  }

  setTimeValue(entry, fieldSuffix, value) {
    // Plus-minus / time-stepper: update hidden field + display span
    const hidden = entry.querySelector(
      `input[type="hidden"][name$="[${fieldSuffix}]"][data-time-stepper-target="hidden"]`,
    )
    if (hidden) {
      hidden.value = value
      const wrapper = hidden.closest("[data-controller='time-stepper']")
      if (wrapper) {
        const display = wrapper.querySelector("[data-time-stepper-target='display']")
        if (display) display.textContent = value || "--:--"
      }
      return
    }

    // Native time input
    const timeInput = entry.querySelector(`input[type="time"][name$="[${fieldSuffix}]"]`)
    if (timeInput) {
      timeInput.value = value
      return
    }

    // Text input (timepicker controller)
    const textInput = entry.querySelector(`input[type="text"][name$="[${fieldSuffix}]"]`)
    if (textInput) {
      textInput.value = value
      return
    }

    // Time picker (time_picker_controller): its hidden value is the source of
    // truth and the visible field follows a change event on it
    // (#valueChangedExternally). Writing the value without the event would submit a
    // time the employee cannot see.
    const picker = entry.querySelector(
      `input[data-time-picker-target="value"][name$="[${fieldSuffix}]"]`,
    )
    if (picker) {
      if (picker.value === value) return
      picker.value = value
      picker.dispatchEvent(new Event("change", { bubbles: true }))
      return
    }

    // A read-only field renders as a plain hidden input
    const plain = entry.querySelector(`input[name$="[${fieldSuffix}]"]`)
    if (plain) plain.value = value
  }

  setBreakTime(entry, value) {
    // Break time stepper (plus_minus for break): hidden + display
    const breakStepperHidden = entry.querySelector("[data-break-time-stepper-target='hidden']")
    if (breakStepperHidden) {
      breakStepperHidden.value = value
      const wrapper = breakStepperHidden.closest("[data-controller='break-time-stepper']")
      if (wrapper) {
        const display = wrapper.querySelector("[data-break-time-stepper-target='display']")
        if (display) display.textContent = "0"
      }
      return
    }

    // Duration input (hours + minutes): hidden + hours/minutes inputs
    const durationHidden = entry.querySelector("[data-duration-input-target='hidden']")
    if (durationHidden) {
      durationHidden.value = value
      const wrapper = durationHidden.closest("[data-controller='duration-input']")
      if (wrapper) {
        const hours = wrapper.querySelector("[data-duration-input-target='hours']")
        const minutes = wrapper.querySelector("[data-duration-input-target='minutes']")
        if (hours) hours.value = "0"
        if (minutes) minutes.value = "0"
      }
      return
    }

    // Break time minutes (minutes only or dropdown): hidden + minutes input/select
    const breakMinutesHidden = entry.querySelector("[data-break-time-minutes-target='hidden']")
    if (breakMinutesHidden) {
      breakMinutesHidden.value = value
      const wrapper = breakMinutesHidden.closest("[data-controller='break-time-minutes']")
      if (wrapper) {
        const minutesInput = wrapper.querySelector("[data-break-time-minutes-target='minutes']")
        if (minutesInput) minutesInput.value = "0"
      }
      return
    }

    // Native time input for break
    const breakTimeInput = entry.querySelector("input[type='time'][name$='[time_break]']")
    if (breakTimeInput) {
      breakTimeInput.value = value
    }
  }

  get removeLabel() {
    return (
      document.querySelector("[data-multi-entry-remove-label]")?.dataset?.multiEntryRemoveLabel ||
      "Entfernen"
    )
  }
}
